import { useState, useEffect } from "react";
import {
    db,
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    where,
    updateDoc,
    doc
} from "../firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function OrdersScreen() {
    /* ---------- state ---------- */
    const [orders, setOrders] = useState([]);
    const [initialOrders, setInitialOrders] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);

    /* modal */
    const [showModal, setShowModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    /* ---------- initial load ---------- */
    useEffect(() => { fetchInitialOrders(); }, []);

    /* 1. primer lote -------------------------------------------------------- */
    const fetchInitialOrders = async () => {
        try {
            setLoading(true);

            const q = query(
                collection(db, "orders"),               // 👈 tu colección de pedidos
                orderBy("createdAt", "desc"),
                limit(30)
            );

            const snapshot = await (await import("firebase/firestore")).getDocs(q);
            if (!snapshot.empty) {
                const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setOrders(docs);
                setInitialOrders(docs);
                setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
                if (snapshot.size < 30) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error al cargar pedidos");
        } finally {
            setLoading(false);
        }
    };

    /* 2. paginación --------------------------------------------------------- */
    const fetchMoreOrders = async () => {
        if (!lastDoc) return;
        try {
            setLoading(true);
            const q = query(
                collection(db, "orders"),
                orderBy("createdAt", "desc"),
                startAfter(lastDoc),
                limit(30)
            );
            const snap = await (await import("firebase/firestore")).getDocs(q);
            if (!snap.empty) {
                const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setOrders(prev => [...prev, ...docs]);
                setInitialOrders(prev => [...prev, ...docs]);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 30) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error al cargar más pedidos");
        } finally {
            setLoading(false);
        }
    };

    /* 3. filtrado en vivo --------------------------------------------------- */
    const filteredOrders = orders.filter(o => {
        const email = o.email?.toLowerCase() || "";
        const orderNum = String(o.orderNumber || "").toLowerCase();
        const status = o.status?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        return email.includes(search) || orderNum.includes(search) || status.includes(search);
    });

    /* 4. búsqueda directa a Firestore -------------------------------------- */
    const handleDatabaseSearch = async () => {
        if (!searchTerm.trim()) {
            toast.info("Ingresa un término de búsqueda");
            return;
        }
        try {
            setLoading(true);
            // Aquí dejamos un ejemplo buscando por orderNumber exacto; 
            // duplica la query si quieres otras combinaciones.
            const q = query(
                collection(db, "orders"),
                where("orderNumber", "==", Number(searchTerm)), // necesita índice si combinas más campos
                limit(30)
            );
            const snap = await (await import("firebase/firestore")).getDocs(q);
            if (!snap.empty) {
                const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setOrders(docs);
                setHasMore(false);
            } else {
                setOrders([]);
                setHasMore(false);
                toast.info("Sin resultados para la búsqueda");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error en la búsqueda");
        } finally {
            setLoading(false);
        }
    };

    /* 5. limpiar búsqueda --------------------------------------------------- */
    const handleClearSearch = () => {
        setOrders(initialOrders);
        setHasMore(true);
        setSearchTerm("");
    };


    /* ---------- helpers ---------- */
    const formatDate = ms =>
        new Date(ms).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

    const productSummary = arr =>
        Array.isArray(arr) && arr.length
            ? arr.map(p => `${p.product_id} (${p.quantity})`).join(", ")
            : "--";


    /* ----- nuevo: abrir / cerrar modal ----- */
    const openModal = order => {
        setSelectedOrder(order);
        setShowModal(true);
    };
    const closeModal = () => {
        setSelectedOrder(null);
        setShowModal(false);
    };

    /* ----- nuevo: actualizar estado -------- */
    const changeStatus = async newStatus => {
        if (!selectedOrder) return;
        try {
            await updateDoc(doc(db, "orders", selectedOrder.id), { status: newStatus });
            toast.success(`Estado actualizado a "${newStatus}"`);

            // refrescamos la lista localmente para evitar otra llamada
            setOrders(prev =>
                prev.map(o =>
                    o.id === selectedOrder.id ? { ...o, status: newStatus } : o
                )
            );
            closeModal();
        } catch (err) {
            console.error(err);
            toast.error("No se pudo actualizar el estado");
        }
    };

    /* ---------- render ---------- */
    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Listado de Pedidos</h1>

            {/* Búsqueda */}
            <div className="form-control mb-4">
                <label className="label">
                    <span className="label-text font-bold">Buscar Pedido</span>
                </label>
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        placeholder="Ingresa email, número de pedido, estado..."
                        className="input input-bordered w-full"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <button
                        className="btn bg-[#e91c24] border-none text-white"
                        onClick={handleDatabaseSearch}
                        disabled={loading}
                    >
                        {loading ? "Buscando..." : "Buscar"}
                    </button>
                    <button
                        className="btn bg-gray-400 border-none text-white"
                        onClick={handleClearSearch}
                        disabled={loading}
                    >
                        Limpiar
                    </button>
                </div>
            </div>

            {/* ------------ tabla ------------ */}
            <div className="bg-base-100 p-4 rounded shadow">
                <h2 className="text-lg font-bold mb-2">Pedidos</h2>

                {filteredOrders.length ? (
                    <table className="table w-full">
                        <thead>
                            <tr>
                                <th># Pedido</th><th>Fecha</th><th>Email</th><th>Productos</th><th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map(o => (
                                <tr
                                    key={o.id}
                                    className="cursor-pointer hover:bg-base-200"
                                    onClick={() => openModal(o)}
                                >
                                    <td>{o.orderNumber ?? "--"}</td>
                                    <td>{o.createdAt ? formatDate(o.createdAt) : "--"}</td>
                                    <td>{o.email ?? "--"}</td>
                                    <td>{productSummary(o.products)}</td>
                                    <td>
                                        {
                                            o.status
                                                ? o.status === "pending"
                                                    ? "Pendiente"
                                                    : o.status
                                                : "--"
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No hay pedidos para mostrar.</p>
                )}

                {/* paginación */}
                {hasMore && initialOrders.length > 0 && (
                    <div className="flex justify-center mt-4">
                        <button
                            className="btn bg-[#e91c24] border-none text-white"
                            onClick={fetchMoreOrders}
                            disabled={loading}
                        >
                            {loading ? "Cargando..." : "Cargar más"}
                        </button>
                    </div>
                )}
            </div>

            {/* ------------ modal ------------ */}
            {showModal && selectedOrder && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <div className="bg-base-100 w-full max-w-xl p-6 rounded shadow-lg">
                        <h3 className="text-2xl font-bold mb-4">
                            Detalles del pedido #{selectedOrder.orderNumber}
                        </h3>

                        <div className="space-y-2 text-sm">
                            <p><strong>Email:</strong> {selectedOrder.email}</p>
                            <p><strong>Fecha:</strong> {formatDate(selectedOrder.createdAt)}</p>
                            <p>
                                <strong>Estado:</strong>{" "}
                                {selectedOrder.status === "pending"
                                    ? "Pendiente"
                                    : selectedOrder.status}
                            </p>
                        </div>

                        <h4 className="mt-4 font-bold">Productos</h4>
                        <ul className="list-disc list-inside space-y-1">
                            {selectedOrder.products?.map((p, idx) => (
                                <li key={idx}>
                                    {p.product_id} — Cantidad: {p.quantity}
                                </li>
                            )) || <li>--</li>}
                        </ul>

                        <div className="mt-6 flex gap-2">
                            {selectedOrder.status === "Pendiente" && (
                                <>
                                    <button
                                        onClick={() => changeStatus("Aceptado")}
                                        className="btn bg-green-600 text-white border-none flex-1"
                                    >
                                        Aceptado
                                    </button>
                                    <button
                                        onClick={() => changeStatus("Rechazado")}
                                        className="btn bg-red-600 text-white border-none flex-1"
                                    >
                                        Rechazado
                                    </button>
                                </>
                            )}
                            <button
                                onClick={closeModal}
                                className="btn bg-gray-400 text-white border-none flex-1"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}